import { __assign, __rest } from "tslib";
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import uniqueId from '../utilities/uniqueId';
import './TextInput.scss';
var DECIMAL_REGEX = /^\d*\.?\d{0,2}$/;
var Sizes = {
    SMALL: 'small',
    MEDIUM: 'medium'
};
var InputWidths = {
    FULL: 'full',
    LARGE: 'large',
    MEDIUM: 'medium',
    SMALL: 'small'
};
var Formats = {
    NONE: 'none',
    NUMBER: 'number',
    DECIMAL: 'decimal'
};
var TextInput = function (_a) {
    var append = _a.append, autoComplete = _a.autoComplete, className = _a.className, description = _a.description, errorMessage = _a.errorMessage, format = _a.format, inputWidth = _a.inputWidth, isDisabled = _a.isDisabled, isRequired = _a.isRequired, label = _a.label, onBlur = _a.onBlur, onChange = _a.onChange, placeholder = _a.placeholder, prepend = _a.prepend, size = _a.size, type = _a.type, _b = _a.value, value = _b === void 0 ? '' : _b, props = __rest(_a, ["append", "autoComplete", "className", "description", "errorMessage", "format", "inputWidth", "isDisabled", "isRequired", "label", "onBlur", "onChange", "placeholder", "prepend", "size", "type", "value"]);
    var _c = useState(false), isFocused = _c[0], setIsFocused = _c[1];
    var containerClass = classnames('TextInput', "TextInput--".concat(size), className);
    var prependElementClasses = classnames('TextInput__prependElement u-flex u-flexJustify-c u-flexAlign-c', "TextInput__prependElement--".concat(size));
    var appendElementClasses = classnames('TextInput__appendElement u-flex u-flexJustify-c u-flexAlign-c', "TextInput__appendElement--".concat(size));
    var inputGroupClasses = classnames('TextInput__inputGroup u-flex', "TextInput__inputGroup--".concat(inputWidth), {
        'TextInput__inputGroup--isInvalid': Boolean(errorMessage),
        'TextInput__inputGroup--hasPrepend': Boolean(prepend),
        'TextInput__inputGroup--hasAppend': Boolean(append)
    });
    var inputId = useRef(uniqueId('input'));
    // `getFormattedNumber` and `getRawNumber` are intended to be the inverse of one another
    // This currently does not handle i10n and so assumes en-US. If/when i10n is implemented,
    // a library to help convert from locale-formatted number strings to raw numbers should be
    // investigated. The Intl JS API only converts from number to string.
    var getFormattedNumber = function (valueToFormat) {
        return valueToFormat === ''
            ? ''
            : Number(valueToFormat).toLocaleString('en-US');
    };
    var getRawNumber = function (formattedNumber) {
        return Number(formattedNumber.replaceAll(',', ''));
    };
    var shouldFormatNumber = format === Formats.NUMBER && !isFocused;
    var displayValue = shouldFormatNumber ? getFormattedNumber(value) : value;
    /**
     * The `input` element requires the `placeholder` prop to be a string or not exist.
     * It cannot be `null`.  In order to get around this issue without modifying more
     * existing code, the below solution creates an object with the `placeholder` prop
     * set or an empty object that will be spread into the `input` in the render function
     * below
     */
    var placeholderProp = placeholder ? { placeholder: placeholder } : {};
    var handleChange = function (event) {
        var newValue = event.target.value;
        if (format === Formats.NUMBER && newValue !== '') {
            newValue = getRawNumber(newValue);
            // If raw value is not a number, return the value unaltered
            if (Number.isNaN(newValue)) {
                newValue = value;
            }
        }
        else if (format === Formats.DECIMAL &&
            newValue !== '' &&
            !DECIMAL_REGEX.test(newValue.replaceAll(',', ''))) {
            newValue = value;
        }
        onChange(newValue);
    };
    return (React.createElement("div", __assign({}, props, { className: containerClass }),
        label && (React.createElement("label", { className: 'TextInput__label', htmlFor: inputId.current },
            isRequired ? React.createElement("span", { "aria-hidden": 'true' }, "* ") : null,
            label)),
        React.createElement("div", { className: inputGroupClasses },
            prepend ? (React.createElement("div", { className: prependElementClasses }, prepend)) : null,
            React.createElement("input", __assign({ autoComplete: autoComplete !== null && autoComplete !== void 0 ? autoComplete : '', className: 'TextInput__input u-flexItem-grow', disabled: isDisabled, id: inputId.current, onBlur: function () {
                    onBlur === null || onBlur === void 0 ? void 0 : onBlur();
                    setIsFocused(false);
                }, onChange: handleChange, onFocus: function () { return setIsFocused(true); }, required: isRequired, type: type, value: displayValue !== null && displayValue !== void 0 ? displayValue : '' }, placeholderProp)),
            append ? React.createElement("div", { className: appendElementClasses }, append) : null),
        errorMessage && (React.createElement("div", { className: 'TextInput__errorMessage', role: 'alert' }, errorMessage)),
        description && (React.createElement("div", { className: 'TextInput__description' }, description))));
};
TextInput.defaultProps = {
    format: Formats.NONE,
    inputWidth: InputWidths.LARGE,
    isDisabled: false,
    isRequired: false,
    onBlur: function () { },
    size: Sizes.MEDIUM,
    type: 'text',
    value: ''
};
TextInput.propTypes = {
    append: PropTypes.node,
    autoComplete: PropTypes.string,
    className: PropTypes.string,
    description: PropTypes.node,
    errorMessage: PropTypes.node,
    format: PropTypes.oneOf(Object.values(Formats)),
    inputWidth: PropTypes.oneOf(Object.values(InputWidths)),
    isDisabled: PropTypes.bool,
    isRequired: PropTypes.bool,
    label: PropTypes.node,
    onBlur: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    prepend: PropTypes.node,
    size: PropTypes.oneOf(Object.values(Sizes)),
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
export default TextInput;
//# sourceMappingURL=TextInput.js.map